import React, { useState, useEffect } from "react";
import {
  HbrDivider,
  HbrHeatmapChart,
} from "@harbor/elements-react-standalone";
import { Typography } from "@mui/material";
import {
  useFetchPartnerServiceCreation,
  useFetchAllPhase,
  useFetchAllServiceCreationActivity,
  useFetchPartner,
  useFetchAllGeo,
  useFetchAllAccountSegment,
  useFetchAllAccountType,
  useFetchAllMarketSegment,
} from "../../hooks/mssp-api-hooks";
import { useParams } from "react-router-dom";
import { RootState } from "../../support/store";
import { useSelector } from "react-redux";

// Rollup logic for determining phase status
const getRollUpStatus = (activities) => {
  const statusCounts = activities.reduce((acc, activity) => {
    acc[activity.status] = (acc[activity.status] || 0) + 1;
    return acc;
  }, {});

  if (statusCounts["Completed"] === activities.length)
    return { label: "Completed", color: "#6BBF41" };
  else if (statusCounts["Blocked"] > 0)
    return { label: "Blocked", color: "#FA5762" };
  else if (statusCounts["Delayed"] > 0)
    return { label: "Delayed", color: "#F7782F" };
  else if (statusCounts["On-Going"] > 0)
    return { label: "On-Going", color: "#F0C243" };
  else if ((statusCounts["In Progress"] > 0 || statusCounts["Completed"] < 0))
    return { label: "In Progress", color: "#1D69CC" };
  return { label: "Not Started", color: "#F0F1F2" };
};

const ServiceCreationHeatMap = ({ refreshTrigger }) => {
  const { id } = useParams();
  const allProducts = useSelector((state: RootState) => state.products.items);
  
  const [heatmapData, setHeatmapData] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { data: partner, refetch: refetchPartner } = useFetchPartner(Number(id));
  const { data: allPhases, refetch: refetchPhases } = useFetchAllPhase();
  const { data: geo } = useFetchAllGeo();
  const { data: accountSegments } = useFetchAllAccountSegment();
  const { data: accountTypes } = useFetchAllAccountType();
  const { data: marketSegments } = useFetchAllMarketSegment();
  const {
    data: partnerServiceCreation,
    refetch: refetchServiceCreation,
  } = useFetchPartnerServiceCreation(Number(id));
  const { data: allActivities, refetch: refetchActivities } =
    useFetchAllServiceCreationActivity();

  // Refetch data when `refreshTrigger` changes
  useEffect(() => {
    const refreshData = async () => {
      setIsRefreshing(true); // Set refreshing state
      try {
        // Trigger refetch for all data
        await Promise.all([
          refetchPhases(),
          refetchServiceCreation(),
          refetchActivities(),
        ]);
      } catch (error) {
        console.error("Failed to refresh heatmap data:", error);
      } finally {
        setIsRefreshing(false); // Reset refreshing state
      }
    };

    refreshData();
  }, [refreshTrigger, refetchActivities, refetchPhases, refetchServiceCreation]);

  useEffect(() => {
    if (allProducts && allPhases && partnerServiceCreation && allActivities) {
      const filteredProducts = allProducts.filter(
        (product) =>
          product.vendor?.is_cisco === true &&
          product.mssp_service_creation === true
      );

      // Prepare heatmap data: Flip axes -> Products on the x-axis, Phases on the y-axis
      const normalizedData = allPhases.map((phase) => {
        return {
          id: phase.name, // Phase name becomes the row (y-axis)
          data: filteredProducts.map((product) => {
            const productServiceCreation = partnerServiceCreation.find(
              (service) =>
                service.product?.id === product.id &&
                service.partner.id === Number(id)
            );

            // Safeguard against undefined productServiceCreation or activities
            const activities = allActivities
              ? allActivities.filter(
                (activity) =>
                  activity.phase === phase.id &&
                  (activity.service_creation === productServiceCreation?.id && activity?.service_creation !== undefined)
              )
              : [];

            // Roll up status for the phase
            const { label, color } =
              activities.length > 0
                ? getRollUpStatus(activities)
                : { label: "Not Started", color: "#F0F1F2" };

            return {
              x: product?.abbreviation
                ? product.abbreviation
                : product.name, // Product name becomes the column
              y: label, // Phase name becomes the row
              label, // Rollup status text
              color, // Color based on roll-up status
            };
          }),
        };
      });

      setHeatmapData(normalizedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allProducts,
    allPhases,
    partnerServiceCreation,
    allActivities,
    refreshTrigger,
  ]);

  const heatmapConfig = {
    margin: { top: 20, bottom: 10, left: 180, right: 30 },
    data: heatmapData,
    axisTop: {
      tickRotation: 0,
      tickSize: 0,
      tickPadding: 5,
    },
    axisBottom: {
      tickRotation: 0,
      tickSize: 0,
      tickPadding: 10,
    },
    axisLeft: {
      tickRotation: 0,
      tickSize: 0,
      tickPadding: 5,
    },
    borderWidth: 0,
    borderRadius: 0,

    enableLabels: true,
    xInnerPadding: 0,
    yInnerPadding: 0,
    labelTextColor: { from: "color", modifiers: [["darker", 1.8]] },
    legends: [],
  };

  if (!partner) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <div className="hbr-css__container-section">
      <div className="hbr-css__grid-lg">
        <div
          className="hbr-css__container-card"
          data-col-span="sm"
          data-tablet-col-span="sm"
        >
          <dl className="atm-key-value-pairs">
            <dt>Account Type</dt>
            <dd>
              {accountTypes?.find((type) => type.id === partner.account_type)
                ?.name || "Unknown"}
            </dd>
            <dt>Managed / Resale</dt>
            <dd>{partner.managed_resale || "Unknown"}</dd>
            <dt>Account Segment</dt>
            <dd>
              {accountSegments?.find(
                (segment) => segment.id === partner.account_segment
              )?.name || "Unknown"}
            </dd>
            <dt>Geo</dt>
            <dd>
              {geo?.find((g) => g.id === partner.geo)?.name || "Unknown"}
            </dd>
            <dt>Market Segments</dt>
            <dd>
              {partner.market_segment
                ?.map(
                  (id) =>
                    marketSegments?.find((segment) => segment.id === id)?.name
                )
                .join(", ") || "Unknown"}
            </dd>
            <dt>Competitive Landscape</dt>
            <dd>{partner.competitive_landscape || ""}</dd>
          </dl>
          <HbrDivider />
          <h4>Cisco Roles</h4>
          <dl className="atm-key-value-pairs">
            <dt>Executive Sponsor</dt>
            <dd>{partner?.cisco_exec_sponsor || ""}</dd>
            <dt>GSSO Business Development Lead</dt>
            <dd>{partner?.gsso_biz_dev_lead || ""}</dd>
            <dt>GSSO Technical Lead</dt>
            <dd>{partner?.gsso_tech_lead || ""}</dd>
          </dl>
          <HbrDivider />
          <h4>Partner Roles</h4>
          <dl className="atm-key-value-pairs">
            <dt>Executive Sponsor</dt>
            <dd>{partner?.partner_exec_sponsor || ""}</dd>
            <dt>Business Development Lead</dt>
            <dd>{partner?.gprs_biz_dev_lead || ""}</dd>
            <dt>Sales Acceleration Lead</dt>
            <dd>{partner?.gprs_sales_accel_pbdm || ""}</dd>
          </dl>
        </div>
        {heatmapData.length > 0 ? (
          <div
            className="hbr-css__container-card"
            data-col-span="lg"
            data-tablet-col-span="lg"
          >
            <HbrHeatmapChart
              config={heatmapConfig}
              width="100%"
              height="450px"
            ></HbrHeatmapChart>
          </div>
        ) : (
          <Typography>Loading Heatmap...</Typography>
        )}
      </div>
    </div>
  );
};

export default ServiceCreationHeatMap;